import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import nullToEmpty from "utils/nullToEmpty";
import { toast } from 'react-toastify';
import NumberFormat, { NumberFormatRemove } from "utils/numberFormat-legacy";
import numberOrdinalSuffix from "utils/numberOrdinalSuffix";
import validate from "utils/validate";
import PageContent from "components/__new/common/PageContent";
import Maybe from "components/__new/common/Maybe";
import { BackButtonPage } from "components/__new/common/BackButton";
import TabSettings from "components/__new/common/TabSettings";
import { Input, SelectInput } from "components/__new/common/form";
import useLoginInfo from "hooks/useLoginInfo";
import * as auth from "store/auth/user/action";
import * as info from "store/entities/settingsInfo/action";

const AddSubAccountCompanyInfo = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const { subAccountForm } = useSelector((s) => s.auth.user);
    const { companyInfoLoading, companyInfo } = useSelector((s) => s.entities.settingsInfo);

    const [form, setForm] = useState({
        companyName: "",
        companySize: "",
        
        invoiceEmail: "",
        rcNumber: "",
        tinNumber: "", 
        address: "",
        estimatedEmployees: "",
        estimatedSalary: "",

        payrollCutOff: "",
        withdrawalPercent: "",
        autoWithdrawals: true,
        paymentFee: "",
    })


    const updateEstimatedSalary = (value) => {
        if (value !== ""){
            setForm({...form, estimatedSalary: NumberFormat(value)})
        }
    }


    const submitForm = async e => {
        e.preventDefault();

        const errors = validate(form);
        if (errors){
            for (var x in errors) {
                toast.error(errors[x]);
            };
            return false;
        }
        else{
            const newForm = {
                ...form,
                estimatedSalary: NumberFormatRemove(form.estimatedSalary),
            }
            let params = {
                ...subAccountForm,
                employerId: companyInfo?.company?.employerId,
                companyId: companyInfo?.company?._id,
                ...newForm,
            };
            dispatch(auth.saveSubAccountForm(params));
            history.push("/account/add/user");
        }
    }


    useEffect(() => {
        if (userInfo?.employer?.accountType === "sub" && Object.keys(userInfo?.mainAccount).length === 0){
            history.replace("/401");
        }
        // eslint-disable-next-line
    }, []);
    

    useEffect(() => {
        if ("sameCompany" in subAccountForm){
            if (subAccountForm.sameCompany === true){
                dispatch(info.getInfo(userInfo?.company?.selectedCompanyId));
            }
            else{
                const params = {
                    companyName: nullToEmpty(subAccountForm.companyName),
                    companySize: nullToEmpty(subAccountForm.companySize),
                    
                    invoiceEmail: nullToEmpty(subAccountForm.invoiceEmail),
                    rcNumber: nullToEmpty(subAccountForm.rcNumber),
                    tinNumber: nullToEmpty(subAccountForm.tinNumber),
                    address: nullToEmpty(subAccountForm.address),
                    estimatedEmployees: nullToEmpty(subAccountForm.estimatedEmployees),
                    estimatedSalary: NumberFormat(subAccountForm.estimatedSalary),
                    
                    payrollCutOff: nullToEmpty(subAccountForm.payrollCutOff),
                    withdrawalPercent: nullToEmpty(subAccountForm.withdrawalPercent),
                    autoWithdrawals: true,
                    paymentFee: nullToEmpty(subAccountForm.paymentFee),
                }
                setForm(params);
            }
        }
        else{
            history.push("/account/add");
        }

        // eslint-disable-next-line
    }, [])


    useEffect(() => {
        if (companyInfoLoading === false && companyInfo?.status === "success"){
            if (subAccountForm.sameCompany === true){
                const params = {
                    companyName: nullToEmpty(subAccountForm.companyName),
                    companySize: nullToEmpty(subAccountForm.companySize),

                    invoiceEmail: companyInfo?.company?.invoiceEmail,
                    rcNumber: companyInfo?.company?.rcNumber,
                    tinNumber: companyInfo?.company?.tinNumber,
                    address: companyInfo?.company?.address,
                    estimatedEmployees: companyInfo?.company?.estimatedEmployees,
                    estimatedSalary: NumberFormat(companyInfo?.company?.estimatedSalary.toFixed(2)),
                    
                    payrollCutOff: companyInfo?.company?.payrollCutOff,
                    withdrawalPercent: companyInfo?.company?.withdrawalPercent,
                    autoWithdrawals: companyInfo?.company?.autoWithdrawals,
                    paymentFee: companyInfo?.company?.paymentFee,
                }
                setForm(params);
            }
        }
        // eslint-disable-next-line
    }, [companyInfo])


    return ( 
        <div>

            <DynamicHeadTag
                headerText="Company Details | Sub Accounts"
                headerDescription={null}
            />


            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title flex">
                        <BackButtonPage />
                        Add Business
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-2 lg:mt-0 lg:justify-end space-x-3"></div>
            </div>


            <div className="mt-6 box">
                <div className="border-b">
                    <TabSettings options={[
                        {
                            title: <span>[1] Business Details</span>,
                            type: "div",
                            isActive: true,
                        },
                        {
                            title: <span>[2] Admin Details</span>,
                            type: "div",
                        },
                        {
                            title: <span>[3] Preview Details</span>,
                            type: "div",
                        },
                    ]} />
                </div>
                

                <div className="mt-6">

                    <PageContent
                        loading={companyInfoLoading}
                        data={companyInfo}
                        loadingText="Fetching company info"
                        emptyText="Company info not found"
                        errorText="Unable to fetch company info"
                    />

                    <Maybe condition={companyInfoLoading === false && companyInfo?.status === "success"}>
                        <form onSubmit={submitForm}>

                            <div>
                                Enter all business details before you proceed.
                            </div>

                            <div className="mt-6 max-w-2xl">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">

                                    <Input type="text" label="Company Name" placeholder="Enter the name of the company" value={form.companyName} onChange={(e) => setForm({...form, companyName: e.target.value})} />
                                    
                                    <SelectInput label="Company Size" required={true} value={form.companySize} onChange={(e) => setForm({...form, companySize: e.target.value})}>
                                        <option value="" disabled>Select number of employees</option>
                                        <option value="1">Less than 50</option>
                                        <option value="2">50 - 100</option>
                                        <option value="3">100 - 200</option>
                                        <option value="4">200 and above</option>
                                    </SelectInput>

                                    <Input
                                        type="email"
                                        label="Payment Email"
                                        placeholder="Enter email address to receive payment notes"
                                        tooltip={<span>This is the email of the team/personnel <br/>that processes payroll at your company. <br/>This may be the HR, Admin, COO.</span>}
                                        value={form.invoiceEmail}
                                        onChange={(e) => setForm({...form, invoiceEmail: e.target.value})}
                                        disabled={subAccountForm.sameCompany === true}
                                    />

                                    <Input type="text" label="RC Number" placeholder="Enter RC number" value={form.rcNumber} onChange={(e) => setForm({...form, rcNumber: e.target.value})} disabled={subAccountForm.sameCompany === true} />
                                    
                                    <Input type="text" label="TIN Number" placeholder="Enter TIN number" value={form.tinNumber} onChange={(e) => setForm({...form, tinNumber: e.target.value})} disabled={subAccountForm.sameCompany === true} />
                                    
                                    <Input type="text" label="Registered address" placeholder="Enter Registered address" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} disabled={subAccountForm.sameCompany === true} />
                                    
                                    <Input type="number" label="Number of Employees" placeholder="Enter Number of Employees" value={form.estimatedEmployees} onChange={(e) => setForm({...form, estimatedEmployees: e.target.value})} disabled={subAccountForm.sameCompany === true} />
                                    
                                    <Input type="text" label="Total Monthly Payroll" placeholder="Enter Total Monthly Payroll" value={form.estimatedSalary} onChange={(e) => updateEstimatedSalary(e.target.value)} disabled={subAccountForm.sameCompany === true} />
                                    
                                    <SelectInput
                                        label="Salary Date (Every month)"
                                        tooltip={<span className='text-justify'>This is your company’s official salary <br/>payment date. This information enables <br/> us know when to disable withdrawals for<br/> the month.</span>}
                                        required={true}
                                        value={form.payrollCutOff}
                                        onChange={(e) => setForm({...form, payrollCutOff: e.target.value})}
                                        disabled={subAccountForm.sameCompany === true}
                                    >
                                        <option value="">Select Day of the Month</option>
                                        {[...Array(31).keys()].slice(1).map((ele, index) =>
                                            <option key={index} value={ele}>{numberOrdinalSuffix(ele)}</option>
                                        )}
                                    </SelectInput>

                                    <SelectInput
                                        label="Salary Withdrawal (%)"
                                        tooltip={<span className='text-justify'>Percentage of earned salaries your employees <br/> can access at each time.</span>}
                                        required={true}
                                        value={form.withdrawalPercent}
                                        onChange={(e) => setForm({...form, withdrawalPercent: e.target.value})}
                                        disabled={subAccountForm.sameCompany === true}
                                    >
                                        <option value="">Select</option>
                                        <option value="50">50%</option>
                                        <option value="75">75%</option>
                                    </SelectInput>
                                    
                                    <SelectInput
                                        label="Payment Fee"
                                        tooltip={<span className='text-justify'>This is the fee charged for each withdrawal of <br/> earned salaries. This can either be paid by the <br/>business, employee or split between the business<br/> and the employee.</span>}
                                        required={true}
                                        value={form.paymentFee}
                                        onChange={(e) => setForm({...form, paymentFee: e.target.value})}
                                        disabled={subAccountForm.sameCompany === true}
                                    >
                                        <option value="">Select</option>
                                        <option value="employee">By Employee</option>
                                        <option value="employer">By Business</option>
                                        <option value="shared">Shared</option>
                                    </SelectInput>

                                </div>

                                <div className="mt-6 flex justify-start space-x-4">
                                    <button type="submit" className="btn btn-ep-primary">
                                        Continue
                                    </button>
                                    <button type="button" onClick={() => history.goBack()} className="btn btn-transparent-primary">
                                        Cancel
                                    </button>
                                </div>
                            </div>

                        </form>
                    </Maybe>

                </div>
            </div>

        </div>
    )
}


export default AddSubAccountCompanyInfo;